// src/components/StatementView.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Autocomplete,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  Grid,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Paper,
  Collapse,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import FilterListIcon from '@mui/icons-material/FilterList';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fmt, sym } from '../utils/currency';
import { isRealDate } from '../utils/dateValidation';
import CompanyHeader from './CompanyHeader';
import { useCompany } from '../hooks/useCompany';

const API = process.env.REACT_APP_API_URL;

const StatementView = () => {
  const navigate = useNavigate();
  const company = useCompany();
  const [admNo, setAdmNo] = useState('');
  const [learners, setLearners] = useState([]); // for name/adm-no search
  const [statement, setStatement] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [docType, setDocType] = useState('all'); // all, invoice, payment

  // Load the learner list once so the search box can match on name or adm no.
  useEffect(() => {
    axios
      .get(`${API}/api/academics/learners`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then(r => setLearners(Array.isArray(r.data) ? r.data : []))
      .catch(() => {}); // search still works by typing an adm no directly
  }, []);

  // Fetch a statement for the given admission number (defaults to the current
  // input). Accepting an argument lets a picked search result fetch immediately
  // without waiting for the admNo state to settle.
  const fetchStatement = async (no) => {
    const id = String(no ?? admNo).trim();
    if (!id || /[:/\\]/.test(id)) {
      setError('Please enter a valid Admission Number (e.g., 7007)');
      return;
    }

    setLoading(true);
    setError('');
    setStatement(null);

    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/finance/statements/${id}`,
        { headers }
      );
      setStatement(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Learner not found or server error');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchStatement = () => fetchStatement();

  // Build the sorted ledger, carrying a running balance. When a Start Date is
  // set, transactions before it are folded into an opening "balance brought
  // forward" instead of being dropped — so the running balance stays correct
  // (BC customer-statement behaviour). Returns { opening, rows }.
  const buildStatement = () => {
    if (!statement) return { opening: 0, rows: [] };

    const all = [];

    // Invoices (debits)
    if (docType === 'all' || docType === 'invoice') {
      statement.invoices.forEach(inv => {
        all.push({
          date: new Date(inv.date),
          type: 'Invoice',
          documentNo: inv.invoiceNo,
          description: inv.description,
          debit: inv.amount,
          credit: 0,
        });
      });
    }

    // Receipts (credits)
    if (docType === 'all' || docType === 'payment') {
      statement.receipts.forEach(rec => {
        all.push({
          date: new Date(rec.paymentDate),
          type: 'Payment',
          documentNo: rec.receiptNo,
          description: rec.transactionDescription,
          debit: 0,
          credit: rec.amount,
        });
      });
    }

    // Credit notes (credits — waivers/corrections)
    if (docType === 'all' || docType === 'credit') {
      (statement.creditNotes || []).forEach(cn => {
        all.push({
          date: new Date(cn.date),
          type: 'Credit Note',
          documentNo: cn.creditNoteNo,
          description: cn.reason || `Credit note (${cn.invoiceNo})`,
          debit: 0,
          credit: cn.amount,
        });
      });
    }

    // Sort by date (oldest first); Array.prototype.sort is stable, so same-day
    // rows keep their insertion order (invoices before payments).
    all.sort((a, b) => a.date - b.date);

    const start = startDate ? new Date(startDate) : null;
    let end = null;
    if (endDate) {
      end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
    }

    let balance = 0;
    let opening = 0;
    const rows = [];

    all.forEach(t => {
      if (start && t.date < start) {
        // Before the period: fold into the balance brought forward.
        opening += t.debit - t.credit;
        balance = opening;
        return;
      }
      if (end && t.date > end) return; // after the period: excluded
      balance += t.debit - t.credit;
      rows.push({ ...t, balance });
    });

    return { opening: start ? opening : 0, rows };
  };

  // Aging of the outstanding balance as of today, bucketed by invoice date
  // (invoices carry no due date). Reflects the whole account, independent of
  // the on-screen date filter. Returns null when nothing is outstanding.
  const getAging = () => {
    if (!statement) return null;
    const today = Date.now();
    const b = { current: 0, d31_60: 0, d61_90: 0, over90: 0 };
    statement.invoices.forEach(inv => {
      const outstanding = (inv.amount || 0) - (inv.paid || 0) - (inv.credited || 0);
      if (outstanding <= 0.005) return;
      const days = Math.floor((today - new Date(inv.date).getTime()) / 86400000);
      if (days <= 30) b.current += outstanding;
      else if (days <= 60) b.d31_60 += outstanding;
      else if (days <= 90) b.d61_90 += outstanding;
      else b.over90 += outstanding;
    });
    const total = b.current + b.d31_60 + b.d61_90 + b.over90;
    return total > 0.005 ? { ...b, total } : null;
  };

  // Load the company logo as a data URL for embedding in the PDF. Fetches the
  // image as a blob (the /logo/download endpoint requires the auth token, and a
  // blob avoids the canvas-tainting that an <img> hits cross-origin) and reads
  // it with FileReader. Resolves to null on any failure so the export still
  // succeeds without the logo.
  const loadLogoDataUrl = async () => {
    try {
      let blob;
      if (company?.logoData) {
        const res = await axios.get(`${API}/api/company/logo/download`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob',
        });
        blob = res.data;
      } else if (company?.logoUrl) {
        const res = await axios.get(company.logoUrl, { responseType: 'blob' });
        blob = res.data;
      } else {
        return null;
      }

      const type = blob.type || '';
      if (type.includes('svg')) return null; // jsPDF addImage can't embed SVG
      const format = type.includes('jpeg') || type.includes('jpg') ? 'JPEG' : 'PNG';

      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(blob);
      });

      // Read intrinsic size from the data URL (no CORS/taint issues) so the
      // logo keeps its aspect ratio in the PDF.
      const dims = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
        img.onerror = () => resolve({ w: 1, h: 1 });
        img.src = dataUrl;
      });

      return { dataUrl, format, w: dims.w, h: dims.h };
    } catch {
      return null;
    }
  };

  const exportToPDF = async () => {
    if (!statement) return;

    const doc = new jsPDF();
    const { learner } = statement;

    // ── Company letterhead ──
    let y = 16;
    const logo = await loadLogoDataUrl();
    if (logo) {
      const h = 16;
      const w = Math.min(40, (logo.w / logo.h) * h);
      doc.addImage(logo.dataUrl, logo.format, 14, y - 4, w, h);
    }
    if (company?.name) {
      doc.setFontSize(15);
      doc.setFont('helvetica', 'bold');
      doc.text(company.name, 105, y, { align: 'center' });
      y += 6;
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const addr = [company?.address, company?.city, company?.county].filter(Boolean).join(', ');
    if (addr) { doc.text(addr, 105, y, { align: 'center' }); y += 4; }
    const contact = [company?.phone, company?.phone2, company?.email].filter(Boolean).join('  ·  ');
    if (contact) { doc.text(contact, 105, y, { align: 'center' }); y += 4; }
    if (company?.kraPin) { doc.text(`Revenue Authority No.: ${company.kraPin}`, 105, y, { align: 'center' }); y += 4; }

    doc.setDrawColor(41, 128, 185);
    doc.setLineWidth(0.5);
    doc.line(14, y, 196, y);
    y += 8;

    // ── Statement title & learner details ──
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('LEARNER STATEMENT', 105, y, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    y += 8;
    doc.setFontSize(11);
    doc.text(`Name: ${learner.name}`, 14, y);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 196, y, { align: 'right' });
    y += 7;
    doc.text(`Admission No: ${learner.admNo}`, 14, y);
    y += 7;
    doc.text(`Stage: ${learner.stage}`, 14, y);
    y += 7;
    if (startDate || endDate) {
      doc.text(`Period: ${startDate || 'Start'} to ${endDate || 'End'}`, 14, y);
      y += 7;
    }

    // ── Ledger table ──
    const tableData = transactions.map(t => [
      t.date.toLocaleDateString(),
      t.type,
      t.documentNo,
      t.description,
      t.debit > 0 ? t.debit.toFixed(2) : '',
      t.credit > 0 ? t.credit.toFixed(2) : '',
      t.balance.toFixed(2),
    ]);

    // Opening "balance brought forward" row (only when a Start Date is set).
    if (startDate) {
      tableData.unshift([
        new Date(startDate).toLocaleDateString(),
        '',
        '',
        'Balance brought forward',
        '',
        '',
        opening.toFixed(2),
      ]);
    }

    autoTable(doc, {
      startY: y,
      head: [['Date', 'Type', 'Document No', 'Description', 'Debit', 'Credit', 'Balance']],
      body: tableData,
      foot: [['', '', '', 'TOTALS', totalDebit.toFixed(2), totalCredit.toFixed(2), finalBalance.toFixed(2)]],
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold', halign: 'center' },
      footStyles: { fillColor: [0, 0, 255], fontStyle: 'bold', halign: 'right' },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 20 },
        2: { cellWidth: 28 },
        3: { cellWidth: 50 },
        4: { halign: 'right', cellWidth: 22 },
        5: { halign: 'right', cellWidth: 22 },
        6: { halign: 'right', cellWidth: 22, fontStyle: 'bold' },
      },
    });

    // ── Aging summary ──
    if (aging) {
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Current', '31–60 days', '61–90 days', 'Over 90 days', 'Total Due']],
        body: [[
          fmt(aging.current),
          fmt(aging.d31_60),
          fmt(aging.d61_90),
          fmt(aging.over90),
          fmt(aging.total),
        ]],
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 2, halign: 'right' },
        headStyles: { fillColor: [70, 70, 70], textColor: 255, fontStyle: 'bold', halign: 'center' },
        columnStyles: { 4: { fontStyle: 'bold' } },
        margin: { left: 14 },
        tableWidth: 168,
      });
    }

    doc.save(`${learner.admNo}${new Date().toLocaleDateString()}_statement.pdf`);
  };

  const exportToExcel = () => {
    if (!statement) return;

    const { learner } = statement;

    const rows = [
      [company?.name || 'LEARNER STATEMENT'],
      ['LEARNER STATEMENT'],
      [`Name: ${learner.name}`],
      [`Admission No: ${learner.admNo}`],
      [`Stage: ${learner.stage}`],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [],
    ];

    if (startDate || endDate) {
      rows.push([`Period: ${startDate || 'Start'} to ${endDate || 'End'}`], []);
    }

    rows.push(['Date', 'Type', 'Document No', 'Description', 'Debit', 'Credit', 'Balance']);

    // Opening "balance brought forward" row (only when a Start Date is set).
    if (startDate) {
      rows.push([new Date(startDate).toLocaleDateString(), '', '', 'Balance brought forward', '', '', opening]);
    }

    transactions.forEach(t => {
      rows.push([
        t.date.toLocaleDateString(),
        t.type,
        t.documentNo,
        t.description,
        t.debit > 0 ? t.debit : '',
        t.credit > 0 ? t.credit : '',
        t.balance,
      ]);
    });

    // Totals
    rows.push([], ['', '', '', 'TOTALS', totalDebit, totalCredit, finalBalance]);

    // Aging summary (as of today)
    if (aging) {
      rows.push(
        [],
        ['AGING (as of today)'],
        ['Current', '31-60 days', '61-90 days', 'Over 90 days', 'Total Due'],
        [aging.current, aging.d31_60, aging.d61_90, aging.over90, aging.total],
      );
    }

    // Quote every field and escape embedded quotes so commas in descriptions
    // don't split into extra columns.
    const csv = rows
      .map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${learner.admNo}_${new Date().toLocaleDateString()}_statement.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const { opening, rows: transactions } = statement ? buildStatement() : { opening: 0, rows: [] };
  const totalDebit = transactions.reduce((sum, t) => sum + t.debit, 0);
  const totalCredit = transactions.reduce((sum, t) => sum + t.credit, 0);
  const finalBalance = transactions.length > 0 ? transactions[transactions.length - 1].balance : opening;
  const aging = statement ? getAging() : null;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, mb: 6 }}>
      <Card elevation={6}>
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={10}>
              <Typography variant="h5" component="h1" gutterBottom color="primary" fontWeight="bold">
                Learner Statement
              </Typography>
            </Grid>
            <Grid item xs={2} sx={{ textAlign: 'right' }}>
              <Tooltip title="Back to Dashboard">
                <IconButton
                  color="primary"
                  onClick={() => navigate('/dashboard')}
                  sx={{
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  <HomeIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 3 }}>
            <Grid item xs={12} sm={8}>
              <Autocomplete
                freeSolo
                options={learners}
                disabled={loading}
                inputValue={admNo}
                getOptionLabel={(o) => (typeof o === 'string' ? o : o.admNo)}
                filterOptions={(opts, { inputValue }) => {
                  const q = inputValue.trim().toLowerCase();
                  if (!q) return opts.slice(0, 50);
                  return opts
                    .filter(
                      (o) =>
                        o.name.toLowerCase().includes(q) ||
                        o.admNo.toLowerCase().includes(q)
                    )
                    .slice(0, 50);
                }}
                renderOption={(props, o) => (
                  <li {...props} key={o.admNo}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {o.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Adm No: {o.admNo}
                        {o.stage ? ` · ${o.stage}` : ''}
                        {o.classCode ? ` · ${o.classCode}` : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') setAdmNo(value);
                }}
                onChange={(_, value) => {
                  if (value && typeof value === 'object') {
                    setAdmNo(value.admNo);
                    fetchStatement(value.admNo); // picked from list → fetch now
                  } else if (typeof value === 'string') {
                    setAdmNo(value);
                  } else {
                    setAdmNo('');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search by Name or Admission Number"
                    onKeyPress={(e) => e.key === 'Enter' && handleFetchStatement()}
                    placeholder="Type a name or adm no, e.g., 7007"
                    error={Boolean(admNo && /[:/\\]/.test(admNo))}
                    helperText={
                      admNo && /[:/\\]/.test(admNo)
                        ? 'Invalid characters'
                        : 'Type a learner name to pick, or enter an admission number'
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleFetchStatement}
                fullWidth
                size="large"
                disabled={loading || !admNo || /[:/\\]/.test(admNo)}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Loading...' : 'Get Statement'}
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {statement && (
            <>
              <CompanyHeader title="Learner Statement" />
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Grid container alignItems="center">
                  <Grid item xs={10}>
                    <Typography variant="h6" gutterBottom>
                      {statement.learner.name} ({statement.learner.admNo})
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Stage: {statement.learner.stage}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: 'right' }}>
                    <Tooltip title="Toggle Filters">
                      <IconButton
                        color="primary"
                        onClick={() => setShowFilters(!showFilters)}
                      >
                        <FilterListIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Box>

              {/* Filter Panel */}
              <Collapse in={showFilters}>
                <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Filter Transactions
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        error={!!(startDate && !isRealDate(startDate))}
                        helperText={startDate && !isRealDate(startDate) ? 'Not a valid date' : ''}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: startDate || undefined }}
                        error={!!((endDate && !isRealDate(endDate)) || (startDate && endDate && endDate < startDate))}
                        helperText={(endDate && !isRealDate(endDate)) ? 'Not a valid date' : (startDate && endDate && endDate < startDate) ? 'End before start' : ''}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        select
                        label="Document Type"
                        value={docType}
                        onChange={(e) => setDocType(e.target.value)}
                        fullWidth
                        SelectProps={{ native: true }}
                      >
                        <option value="all">All Transactions</option>
                        <option value="invoice">Invoices Only</option>
                        <option value="payment">Payments Only</option>
                        <option value="credit">Credit Notes Only</option>
                      </TextField>
                    </Grid>
                  </Grid>
                </Paper>
              </Collapse>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={exportToPDF}
                    fullWidth
                    startIcon={<PictureAsPdfIcon />}
                  >
                    Export PDF
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={exportToExcel}
                    fullWidth
                    startIcon={<DescriptionIcon />}
                  >
                    Export Excel
                  </Button>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 3 }} />

              {/* Statement Table */}
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'primary.main' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Document No</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Debit</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Credit</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Opening balance brought forward (when a start date is set) */}
                    {startDate && (
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell>{new Date(startDate).toLocaleDateString()}</TableCell>
                        <TableCell colSpan={3} sx={{ fontStyle: 'italic' }}>
                          Balance brought forward
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'text.secondary' }}>-</TableCell>
                        <TableCell align="right" sx={{ color: 'text.secondary' }}>-</TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 'bold', color: opening >= 0 ? 'error.main' : 'success.main' }}
                        >
                          {opening.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    )}
                    {transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No transactions found for the selected filters
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactions.map((t, idx) => (
                        <TableRow 
                          key={`${t.documentNo}-${idx}`}
                          hover
                          sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}
                        >
                          <TableCell>{t.date.toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color: t.type === 'Invoice' ? 'error.main' : 'success.main',
                                fontWeight: 500,
                              }}
                            >
                              {t.type}
                            </Typography>
                          </TableCell>
                          <TableCell>{t.documentNo}</TableCell>
                          <TableCell>{t.description}</TableCell>
                          <TableCell align="right" sx={{ color: t.debit > 0 ? 'error.main' : 'text.secondary' }}>
                            {t.debit > 0 ? t.debit.toFixed(2) : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: t.credit > 0 ? 'success.main' : 'text.secondary' }}>
                            {t.credit > 0 ? t.credit.toFixed(2) : '-'}
                          </TableCell>
                          <TableCell 
                            align="right" 
                            sx={{ 
                              fontWeight: 'bold',
                              color: t.balance >= 0 ? 'error.main' : 'success.main'
                            }}
                          >
                            {t.balance.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                    {/* Totals Row */}
                    {transactions.length > 0 && (
                      <TableRow sx={{ bgcolor: 'grey.200' }}>
                        <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold' }}>
                          TOTALS:
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                          {totalDebit.toFixed(2)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          {totalCredit.toFixed(2)}
                        </TableCell>
                        <TableCell 
                          align="right" 
                          sx={{ 
                            fontWeight: 'bold', 
                            fontSize: '1.1rem',
                            color: finalBalance >= 0 ? 'error.main' : 'success.main'
                          }}
                        >
                          {finalBalance.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              {/* Summary Box */}
              <Box sx={{ mt: 3, p: 3, bgcolor: finalBalance >= 0 ? 'error.light' : 'success.light', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Debits (Invoices):
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {sym()} {fmt(totalDebit)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Credits (Payments + Notes):
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {sym()} {fmt(totalCredit)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {finalBalance >= 0 ? 'Amount Due:' : 'Overpayment:'}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {sym()} {fmt(Math.abs(finalBalance))}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Aging of the outstanding balance (as of today) */}
              {aging && (
                <Paper elevation={2} sx={{ mt: 3, p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Aging of Outstanding Balance (as of {new Date().toLocaleDateString()})
                  </Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.800' }}>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Current</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>31–60 days</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>61–90 days</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Over 90 days</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Total Due</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell align="right">{sym()} {fmt(aging.current)}</TableCell>
                          <TableCell align="right">{sym()} {fmt(aging.d31_60)}</TableCell>
                          <TableCell align="right">{sym()} {fmt(aging.d61_90)}</TableCell>
                          <TableCell align="right" sx={{ color: aging.over90 > 0 ? 'error.main' : 'inherit' }}>
                            {sym()} {fmt(aging.over90)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{sym()} {fmt(aging.total)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                </Paper>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default StatementView;